import * as React from 'react'
import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useSelectedAgent } from '@/contexts/SelectedAgentContext'
import { useAppNavigate } from '@/hooks/useAppNavigate'
import { DocumentDetailsModal } from '@/features/documents/components/DocumentDetailsModal'
import { DocumentListCard } from '@/features/documents/components/DocumentListCard'
import {
  DOCUMENT_TYPES,
  DOCUMENT_TYPE_ORDER,
  DEFAULT_DOCUMENT_TYPE,
} from '@/features/documents/documentTypes'
import {
  useDocumentTableQuery,
  type DocumentRow,
} from '@/features/documents/hooks/useDocumentTableQuery'
import type { DocumentDetailsInitial } from '@/features/documents/components/DocumentDetailsModal'
import type { DocumentRecord, DocumentType } from '@/features/documents/types'

type ListTab = 'documents' | 'archived'

const INITIAL_SKELETON_KEYS = ['s1', 's2', 's3']

type ModalState =
  | { mode: 'create'; defaultType: DocumentType }
  | {
      mode: 'edit'
      documentId: string
      initial: Partial<DocumentDetailsInitial>
    }

export function DocumentsList() {
  const [tab, setTab] = React.useState<ListTab>('documents')
  const [modalState, setModalState] = React.useState<ModalState | null>(null)
  const { toTask } = useAppNavigate()
  const { selectedAgentId } = useSelectedAgent()

  const archived = tab === 'archived'
  const queryResult = useDocumentTableQuery({
    limit: 25,
    sort_by: 'updated_at',
    sort_order: 'desc',
    archived,
  })

  const documents = React.useMemo(
    () => queryResult.data?.pages.flatMap((p) => p.data) ?? [],
    [queryResult.data]
  )
  const { hasNextPage, isLoading, isFetchingNextPage, fetchNextPage } =
    queryResult

  const handleSelectTab = React.useCallback(
    (value: string) => setTab(value as ListTab),
    []
  )
  const openCreate = React.useCallback(
    (defaultType: DocumentType) =>
      setModalState({ mode: 'create', defaultType }),
    []
  )
  const openEdit = React.useCallback(
    (row: DocumentRow) =>
      setModalState({
        mode: 'edit',
        documentId: row.id,
        initial: {
          name: row.name ?? '',
          type: row.metadata?.type,
          access: row.access ?? [],
          description: row.description ?? '',
        },
      }),
    []
  )

  const handleSubmitted = React.useCallback(
    (record: DocumentRecord) => {
      // Only navigate on create; edit just refreshes the list in place.
      // The modal is conditionally mounted, so closing unmounts it (removing
      // the Radix portal immediately) before navigation flips the tab.
      if (modalState?.mode === 'create') {
        toTask(`/docs/${record.id}`, { replace: false })
      }
    },
    [modalState, toTask]
  )

  const sentinelRef = React.useCallback(
    (node: HTMLDivElement | null) => {
      if (!node) return
      const observer = new IntersectionObserver(([entry]) => {
        if (entry?.isIntersecting) fetchNextPage()
      })
      observer.observe(node)
      return () => observer.disconnect()
    },
    [fetchNextPage]
  )

  function renderList() {
    if (isLoading) {
      return INITIAL_SKELETON_KEYS.map((key) => (
        <DocumentCardSkeleton key={key} />
      ))
    }
    if (queryResult.isError) {
      return <ErrorState onRetry={() => queryResult.refetch()} />
    }
    if (documents.length === 0) {
      return <EmptyState tab={tab} />
    }
    return (
      <>
        {documents.map((document) => (
          <DocumentListCard
            key={document.id}
            document={document}
            archived={archived}
            onEdit={openEdit}
          />
        ))}
        {isFetchingNextPage && <DocumentCardSkeleton key="loading" />}
        {hasNextPage && !isFetchingNextPage && (
          <div ref={sentinelRef} aria-hidden="true" className="h-px" />
        )}
      </>
    )
  }

  return (
    <Tabs
      value={tab}
      onValueChange={handleSelectTab}
      className="flex flex-col gap-1 h-full">
      <div className="flex items-center justify-between p-4">
        {/* Plain text tabs: no chrome variant on the list. */}
        <TabsList className="w-auto gap-3 px-3">
          <TabTrigger value="documents">Documents</TabTrigger>
          <TabTrigger value="archived">Archived</TabTrigger>
        </TabsList>
        <div className="flex items-center">
          <Button
            size="sm"
            className="rounded-r-none"
            onClick={() => openCreate(DEFAULT_DOCUMENT_TYPE)}>
            Create new
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                size="sm"
                aria-label="Choose document type"
                className="rounded-l-none border-l border-primary-foreground/20 px-2">
                <ChevronDown className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {DOCUMENT_TYPE_ORDER.map((value) => {
                const { label, icon: Icon, colorClass } = DOCUMENT_TYPES[value]
                return (
                  <DropdownMenuItem
                    key={value}
                    onSelect={() => openCreate(value)}>
                    <Icon className={`size-4 ${colorClass}`} />
                    {label}
                  </DropdownMenuItem>
                )
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Neutralize the TabBar content's forced flex-1/min-h-0 so the list
          keeps its original flow + scroll behavior. */}
      <TabsContent
        value={tab}
        className="flex-col gap-3 overflow-y-auto px-4 pb-4 data-[state=active]:flex-initial data-[state=active]:min-h-auto">
        {renderList()}
      </TabsContent>

      {/* Conditionally mounted: closing unmounts the Radix portal immediately,
          which avoids it stranding over the editor when create navigates and
          React Activity pauses the exit-animation of an inactive tab. */}
      {modalState && (
        <DocumentDetailsModal
          open
          onOpenChange={(open) => {
            if (!open) setModalState(null)
          }}
          mode={modalState.mode}
          agentId={selectedAgentId}
          documentId={
            modalState.mode === 'edit' ? modalState.documentId : undefined
          }
          initial={modalState.mode === 'edit' ? modalState.initial : undefined}
          defaultType={
            modalState.mode === 'create' ? modalState.defaultType : undefined
          }
          onSubmitted={handleSubmitted}
        />
      )}
    </Tabs>
  )
}

function TabTrigger({
  value,
  children,
}: {
  value: ListTab
  children: React.ReactNode
}) {
  return (
    <TabsTrigger
      value={value}
      // The two text colors came from the deleted `pill` variant; the rest
      // neutralizes chrome the primitive no longer applies.
      className="h-auto min-w-0 cursor-pointer rounded-none px-0 py-0 font-normal hover:bg-transparent data-[state=inactive]:text-muted-foreground data-[state=active]:bg-transparent hover:text-primary-high-contrast data-[state=active]:text-primary-high-contrast">
      {children}
    </TabsTrigger>
  )
}

function DocumentCardSkeleton() {
  return (
    <Card className="flex-row items-center justify-between gap-4 px-5 py-5">
      <div className="flex items-start gap-4 min-w-0 flex-1">
        <Skeleton className="size-[22px] shrink-0 mt-0.5 rounded-md" />
        <div className="flex flex-col gap-2 min-w-0 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/3" />
        </div>
      </div>
    </Card>
  )
}

function ErrorState({ onRetry }: { onRetry: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 text-center py-12">
      <p className="text-muted-foreground">
        Couldn’t load documents. Please try again.
      </p>
      <Button size="sm" variant="outline" onClick={onRetry}>
        Retry
      </Button>
    </div>
  )
}

function EmptyState({ tab }: { tab: ListTab }) {
  if (tab === 'archived') {
    return (
      <div className="text-center text-muted-foreground py-12">
        No archived documents.
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center justify-center gap-3 text-center py-12">
      <p className="text-muted-foreground">
        No documents yet — click Create new to get started.
      </p>
    </div>
  )
}
